/**
 * customers-data — the ONE data layer for the Customers surface (list, detail,
 * column settings). Every read and write in this family goes through here.
 *
 * WHY THIS FILE EXISTS AT ALL, rather than direct typed-client calls:
 *
 * The 2026-09-10 drop names seven operations as the binding contract. Four of
 * them are NOT callable from `@v0y4ge/api-client` today, and one is callable
 * but with a query builder that silently drops the drop's new parameters:
 *
 *  · `getCustomersSettings` / `updateCustomersSettings` — absent (CC dep 1).
 *  · `listBookingCustomers` — present (`booking.ts`), but its query builder
 *    folds `q`/`email`/`phone` onto one `search` param and knows nothing of
 *    `customerTypeId`, `untyped`, `sort`, `direction`, `externalIdSystem`.
 *    Calling it would drop server filtering and sorting on the floor, which is
 *    exactly what acceptance #4 forbids — so this module issues the contract's
 *    query through the `apiRaw` choke point under the SAME operation id, and
 *    the call collapses to the typed fn the day the builder is re-shaped.
 *  · `getCustomer`, `getCustomerServiceRecord` — contracts exist, no client fn.
 *  · `getAdminCustomerMessagingPreferences` — absent under this name/path.
 *  · `getCustomerListFieldCatalog` — PROPOSED, not shipped, not callable
 *    (CC dep 4). The Columns panel treats a failure here as a catalog failure
 *    with Retry, never as an empty catalog, so the surface is honest before
 *    and after the operation lands.
 *
 * Nothing here pretends a pending operation is released: each one is tagged for
 * <DataSourceStrip>, so a screen served by the CD preview seed layer says so on
 * the screen itself.
 *
 * Scope: every read is guarded by the CONFIRMED tenant slug + tenant
 * generation. A response that arrives after a tenant, customer or filter change
 * is dropped, never rendered and never used to confirm a write.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";
import { getConfirmedTenantSlug, getTenantGeneration, onTenantChange, onTenantIdentityResolved, onTenantSwitching } from "../../shell/access";
import { isDenied, messageOf, statusOf } from "./customer-types";

/* ── operation ids, for <DataSourceStrip> ──────────────────────────────── */
export const CUSTOMER_OPS = [
  "getCustomersSettings", "updateCustomersSettings", "listBookingCustomers",
  "getCustomer", "getCustomerServiceRecord", "getAdminCustomerMessagingPreferences",
  "listCustomerTypes", "assignCustomerType", "getCustomerListFieldCatalog",
] as const;

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

const jsonBody = (method: string, body: unknown): RequestInit =>
  ({ method, headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

/* ── list configuration ────────────────────────────────────────────────── */

export type CustomerListSortKey =
  | "name" | "customerType" | "lastSeenAt" | "createdAt"
  | "appointments" | "eventRegistrations" | "eventCheckIns" | "queueJoins";

export type ExternalIdSystem = "sis" | "card" | "lms" | "directory";

export interface CustomersSettings {
  columns: string[];
  defaultSort: { key: CustomerListSortKey; direction: "asc" | "desc" };
  externalIdSystem: ExternalIdSystem | null;
  version: number;
}

/** GET /v1/admin/settings/customers. Read permission is `settings.read` on the
 *  shipped build; CC dep 1 corrects it to `customers.read` so directory readers
 *  can obtain their own rendering configuration. A 403 here is therefore a
 *  KNOWN pre-correction state and is surfaced as such — never silently replaced
 *  with the platform default columns, which would show one tenant another
 *  tenant's layout and call it theirs. */
export const getCustomersSettings = (): Promise<{ data: CustomersSettings }> =>
  tag("getCustomersSettings", apiRaw<{ data: CustomersSettings }>("/v1/admin/settings/customers", undefined, "getCustomersSettings"));

export interface CustomersSettingsUpdate {
  columns: string[];
  defaultSort: { key: CustomerListSortKey; direction: "asc" | "desc" };
  externalIdSystem: ExternalIdSystem | null;
  expectedVersion: number;
}

/** PUT /v1/admin/settings/customers, permission `settings.write`. The typed
 *  client wraps the wire body as `{data: …}` and callers do not wrap it again;
 *  this module IS the wire call while that client fn is pending, so it does the
 *  wrapping here and nowhere else. Version-checked: a stale `expectedVersion`
 *  returns 409 CUSTOMER_LIST_SETTINGS_CONFLICT. */
export const updateCustomersSettings = (body: CustomersSettingsUpdate): Promise<{ data: CustomersSettings }> =>
  tag("updateCustomersSettings", apiRaw<{ data: CustomersSettings }>("/v1/admin/settings/customers", jsonBody("PUT", { data: body }), "updateCustomersSettings"));

/* ── directory ─────────────────────────────────────────────────────────── */

export interface CustomerDirectoryRecord {
  id: string;
  displayName: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  locale?: string | null;
  timezone?: string | null;
  customerType: { id: string; name: string } | null;
  /** Present on the row only when the configured external-ID system matches. */
  externalId: { system: string; namespace: string; value: string } | null;
  classYear?: string | number | null;
  program?: string | null;
  campusOfRecord?: string | null;
  location: { id: string; name: string } | null;
  counts: { appointments: number; eventRegistrations: number; eventCheckIns: number; queueJoins: number };
  lastSeenAt?: string | null;
  createdAt?: string | null;
  marketingConsent?: boolean | null;
  attributes: {
    sis: Record<string, string | number | boolean | null>;
    custom: Record<string, string | number | boolean | null>;
  };
  /** Assignment id, for the type badge when the catalog cannot be read. */
  customerTypeId?: string | null;
}

export interface DirectoryQuery {
  q?: string;
  customerTypeId?: string | null;
  untyped?: boolean;
  /** Narrow the directory to the people registered for ONE event (2026-09-14,
   *  operator: the Events drawer's "open the full list" must land in the
   *  Customers directory, not the retired Guests screen). NOT in the shipped
   *  contract yet — CD-API-REQUEST-customers-event-filter-2026-09-14.md. The
   *  design environment serves it; a backend that ignores the param answers the
   *  unfiltered directory, which the banner in CustomersList makes visible
   *  rather than passing an unfiltered list off as an event roster. */
  eventId?: string | null;
  locationId?: string | null;
  externalIdSystem?: ExternalIdSystem | null;
  sort?: CustomerListSortKey;
  direction?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export interface DirectoryPage {
  data: CustomerDirectoryRecord[];
  pagination: { page: number; limit: number; total: number; hasMore: boolean };
  summary: {
    customers: number;
    appointments: number;
    eventCheckIns: number;
    queueJoins: number;
    customerTypes: Array<{ id: string; name: string; count: number }>;
    untyped: number;
    /** Echoed only for an `eventId` query — the event the list was narrowed to,
     *  so the banner can name it instead of printing an id. Absent means the
     *  responder did not honour the filter. */
    event?: { id: string; title: string } | null;
  };
}

export const DIRECTORY_LIMIT_DEFAULT = 50;
export const DIRECTORY_LIMIT_MAX = 200;

/** GET /v1/admin/customers (`listBookingCustomers`), permission `customers.read`.
 *  `customerTypeId` and `untyped=true` are mutually exclusive — enforced here so
 *  no caller can put both on the wire. Company/tenant scope is the backend's to
 *  decide from the actor's grants: this never forces a session-company filter
 *  onto a tenant-wide grant. */
export function listBookingCustomers(params: DirectoryQuery = {}): Promise<DirectoryPage> {
  const untyped = params.untyped === true && !params.customerTypeId;
  return tag("listBookingCustomers", apiRaw<DirectoryPage>(
    `/v1/admin/customers${query({
      q: params.q,
      eventId: params.eventId ?? undefined,
      customerTypeId: untyped ? undefined : params.customerTypeId ?? undefined,
      untyped: untyped ? true : undefined,
      locationId: params.locationId ?? undefined,
      externalIdSystem: params.externalIdSystem ?? undefined,
      sort: params.sort,
      direction: params.direction,
      page: params.page,
      limit: Math.min(params.limit ?? DIRECTORY_LIMIT_DEFAULT, DIRECTORY_LIMIT_MAX),
    })}`,
    undefined, "listBookingCustomers",
  ));
}

/* ── identity ──────────────────────────────────────────────────────────── */

export interface ContactPoint {
  value: string;
  normalized?: string;
  purpose?: string;
  verification?: string;
  provenance?: string;
  createdAt?: string;
  updatedAt?: string;
}
export interface PostalAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postcode?: string;
  country?: string;
  purpose?: string;
  provenance?: string;
}

export interface CustomerIdentity {
  customerId: string;
  tenantId?: string;
  companyId?: string;
  version?: number;
  status: "active" | "merged" | "restricted";
  mergedIntoCustomerId?: string;
  name: { displayName?: string; firstName?: string; lastName?: string };
  emails: ContactPoint[];
  phones: ContactPoint[];
  postalAddresses: PostalAddress[];
  locale?: string;
  timezone?: string;
  attributes: Record<string, unknown>;
  consent: { marketing: boolean | null };
  createdAt?: string;
  updatedAt?: string;
  /** CC dep 2 — canonical point-read additions, not yet released. Absent today;
   *  every consumer below treats absence as "not supplied", never as "none". */
  customerTypeId?: string | null;
  externalIds?: Array<{ system: string; namespace: string; value: string }>;
  sisAttributes?: Record<string, unknown> | null;
}

/** GET /v1/customers/{customerId}, permission `customers.read`. `redirectTo`
 *  accompanies a merged record and is the navigation target — the UI never
 *  guesses one from `mergedIntoCustomerId`. */
export const getCustomer = (customerId: string): Promise<{ data: CustomerIdentity; redirectTo?: string }> =>
  tag("getCustomer", apiRaw<{ data: CustomerIdentity; redirectTo?: string }>(
    `/v1/customers/${encodeURIComponent(customerId)}`, undefined, "getCustomer"));

/* ── service record (history) ──────────────────────────────────────────── */

export type ServiceRecordDepartment = "appointments" | "events" | "queues";
export type ServiceRecordLifecycleState =
  | "requested" | "waitlisted" | "confirmed" | "inProgress" | "completed" | "cancelled" | "noShow";

export interface ServiceRecordEntry {
  entryId: string;
  department: ServiceRecordDepartment;
  modality?: string;
  sourceRef?: string;
  occurredAt: string;
  lifecycleState: ServiceRecordLifecycleState;
  serviceId?: string | null;
  staffId?: string | null;
  locationId?: string | null;
  container?: string | null;
  title?: string | null;
  staffName?: string | null;
  locationName?: string | null;
  containerTitle?: string | null;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  timezone?: string | null;
}

export interface ServiceRecordPage {
  data: ServiceRecordEntry[];
  meta: {
    customerId: string;
    from?: string | null;
    to?: string | null;
    limit: number;
    truncated: boolean;
    departments: ServiceRecordDepartment[];
  };
}

export const SERVICE_RECORD_LIMIT_MAX = 200;

/** GET /v1/customers/{customerId}/service-record, permission
 *  `customers.service_record.read`. `from` inclusive, `to` exclusive.
 *
 *  CC dep 6: continuation advances the exclusive `to` bound to the oldest
 *  occurrence, which SKIPS entries sharing that timestamp. Until CC ships
 *  lossless semantics the UI reports truncation honestly and never claims a
 *  complete history — see `continuationBound` below, which is deliberately the
 *  only place that bound is computed. */
export const getCustomerServiceRecord = (
  customerId: string,
  params: { from?: string; to?: string; limit?: number } = {},
): Promise<ServiceRecordPage> =>
  tag("getCustomerServiceRecord", apiRaw<ServiceRecordPage>(
    `/v1/customers/${encodeURIComponent(customerId)}/service-record${query({
      from: params.from, to: params.to, limit: Math.min(params.limit ?? SERVICE_RECORD_LIMIT_MAX, SERVICE_RECORD_LIMIT_MAX),
    })}`, undefined, "getCustomerServiceRecord"));

/** The oldest occurrence in a page — the next exclusive `to`. Null when the
 *  page is empty (nothing left to ask for). */
export function continuationBound(entries: ServiceRecordEntry[]): string | null {
  let oldest: string | null = null;
  for (const e of entries) if (!oldest || e.occurredAt < oldest) oldest = e.occurredAt;
  return oldest;
}

/* ── messaging consent (read-only) ─────────────────────────────────────── */

export type ConsentTopic = "bookings" | "events" | "general";
export const CONSENT_TOPICS: ConsentTopic[] = ["bookings", "events", "general"];
/** The three channels Studio renders preferences for. Suppressions may name any
 *  value from the delivery-channel enum — `suppressionChannelLabel` handles a
 *  channel this list does not contain rather than assuming three strings. */
export const CONSENT_CHANNELS = ["email", "sms", "whatsapp"] as const;
export type ConsentChannel = (typeof CONSENT_CHANNELS)[number];
/** WhatsApp is OPT-IN: missing or false does not permit sending. Email and SMS
 *  are OPT-OUT: an unset preference is not an explicit refusal. */
export const CHANNEL_OPT_IN: Record<ConsentChannel, boolean> = { email: false, sms: false, whatsapp: true };

export interface MessagingSuppression {
  channel: string;
  topic?: string | null;
  reason?: string | null;
  source?: string | null;
  createdAt?: string | null;
  address?: string | null;
  code?: string | null;
}

export interface MessagingPreferences {
  customerId: string;
  preferences: Partial<Record<ConsentChannel, Partial<Record<ConsentTopic, boolean>>>>;
  suppressions: MessagingSuppression[];
}

/** GET /v1/admin/customers/{customerId}/messaging-preferences, permission
 *  `customers.read`. Studio uses the ADMIN operation; the customer-session
 *  preference operation is not an admin read and offers mutation this surface
 *  must not have. */
export const getAdminCustomerMessagingPreferences = (customerId: string): Promise<{ data: MessagingPreferences }> =>
  tag("getAdminCustomerMessagingPreferences", apiRaw<{ data: MessagingPreferences }>(
    `/v1/admin/customers/${encodeURIComponent(customerId)}/messaging-preferences`, undefined, "getAdminCustomerMessagingPreferences"));

/* ── available-field catalog (CC dep 4 — PROPOSED, NOT SHIPPED) ────────── */

export type FieldSource = "standard" | "sis" | "custom";
export type FieldDisplayType = "text" | "number" | "date" | "boolean" | "badge" | "identifier";

export interface CustomerListField {
  /** A valid `CustomerListColumnId`: a first-class id, or `sis:<key>` /
   *  `custom:<key>` with `[a-z0-9_]{1,64}`. */
  id: string;
  source: FieldSource;
  /** Translation key; preferred over `label`. */
  labelKey?: string | null;
  /** Authoritative label from the tenant field definition. */
  label?: string | null;
  displayType: FieldDisplayType;
  sortable?: boolean;
  /** False when the actor may see the field exists but not select it. The
   *  catalog does not list fields the actor may not see at all. */
  available?: boolean;
  /** 0 for Name — locked to the first position. */
  requiredPosition?: number | null;
}

export interface CustomerListFieldCatalog {
  fields: CustomerListField[];
}

/** GET /v1/admin/settings/customers/fields — the authoritative inventory of
 *  every selectable customer field (standard, SIS-mapped, custom), INCLUDING
 *  valid fields that no loaded customer has a value for yet.
 *
 *  NOT CALLABLE TODAY (CC dep 4). This is the proposed name and shape, offered
 *  for agreement; until it lands every request 404s and the Columns panel shows
 *  its retryable catalog-failure state. The inventory is deliberately NOT
 *  inferred from the loaded page — 50 rows are a sample, not a schema — and no
 *  customer scan is performed to discover it. */
export const getCustomerListFieldCatalog = (): Promise<{ data: CustomerListFieldCatalog }> =>
  tag("getCustomerListFieldCatalog", apiRaw<{ data: CustomerListFieldCatalog }>(
    "/v1/admin/settings/customers/fields", undefined, "getCustomerListFieldCatalog"));

/* ── error classification ──────────────────────────────────────────────── */

export interface ReadFailure {
  /** Human-readable, never empty. */
  message: string;
  status: number;
  /** 401/403 — a retry cannot help. */
  denied: boolean;
  /** 404 on an identity read — a not-found page, not a generic failure. */
  notFound: boolean;
}

export function classify(e: unknown): ReadFailure {
  const status = statusOf(e);
  const denied = isDenied(e);
  const raw = messageOf(e).trim();
  const message = raw
    || (status === 404 ? "This record could not be found."
      : status === 0 ? "The request could not be completed."
        : `The request failed with status ${status}.`);
  return { message, status, denied, notFound: status === 404 };
}

/* ── scoped reads ──────────────────────────────────────────────────────── */

export interface ScopedRead<T> {
  data: T | null;
  loading: boolean;
  failure: ReadFailure | null;
  reload: () => void;
}

/**
 * Read `fn` and keep the result ONLY while it still belongs to the current
 * scope. A response is dropped when the tenant slug changed, the tenant
 * generation moved (a switch completed), or `key` changed (customer, filters,
 * page, sort) while the request was in flight. A failed read is a `failure`,
 * never an empty result.
 *
 * `enabled: false` reads nothing and reports neither data nor failure — the
 * shape a missing capability takes, so a caller renders "hidden", not "empty".
 *
 * A response for a scope that has since moved is DROPPED but still resolves
 * `loading` (CD docket 2026-09-15 item 3). Leaving it pending made "the
 * response was for a stale scope" and "the response never came" the same
 * state, which is how a reload of the directory could sit on a skeleton
 * forever with no error and no request in flight.
 */
export function useScopedRead<T>(key: string, fn: () => Promise<T>, enabled = true): ScopedRead<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [nonce, setNonce] = useState(0);
  const gen = useRef(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    gen.current += 1;
    const mine = gen.current;
    const scope = getConfirmedTenantSlug();
    const tenantGeneration = getTenantGeneration();
    /* Still the request this effect run started? Cheaper than `current()` and
     * true even when the scope moved underneath us — which is what lets a
     * discarded response resolve `loading` instead of stranding it. */
    const mineStill = () => mine === gen.current;
    const current = () =>
      mineStill() && scope === getConfirmedTenantSlug() && tenantGeneration === getTenantGeneration();

    if (!enabled) { setData(null); setFailure(null); setLoading(false); return; }
    setLoading(true);
    fnRef.current()
      .then((res) => { if (current()) { setData(res); setFailure(null); } })
      .catch((e) => { if (current()) { setData(null); setFailure(classify(e)); } })
      .finally(() => {
        if (!mineStill()) return;              // superseded by a newer request — it owns `loading`
        setLoading(false);
        // Answered for a scope that is no longer ours: the data was dropped
        // above, so re-issue for the scope we are in now rather than leaving
        // the caller with neither data nor a failure.
        if (!current()) setNonce((n) => n + 1);
      });

    return () => { gen.current += 1; };
  }, [key, enabled, nonce]);

  // A tenant switch invalidates whatever is on screen before the new read lands.
  useEffect(() => {
    const off = onTenantSwitching(() => { gen.current += 1; setData(null); setFailure(null); setLoading(enabled); });
    const offChange = onTenantChange(() => setNonce((n) => n + 1));
    /* On a fresh page load the screen can mount before the tenant identity has
     * resolved, so the read above captures a null scope. This is the moment the
     * confirmed slug appears; re-read for it. Cheap and idempotent — a read
     * that already had a real scope re-issues once at worst, and the reload
     * path (which previously never issued the request at all) is fixed. */
    const offResolved = onTenantIdentityResolved(() => setNonce((n) => n + 1));
    return () => { off(); offChange(); offResolved(); };
  }, [enabled]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, loading, failure, reload };
}

/** A stable "is this still the scope I started in?" probe for WRITES, which
 *  cannot use the hook above (they are not renders). Capture before the await,
 *  call after it, and drop everything the call would have done if it is false. */
export function scopeProbe(): () => boolean {
  const scope = getConfirmedTenantSlug();
  const generation = getTenantGeneration();
  return () => scope !== null && scope === getConfirmedTenantSlug() && generation === getTenantGeneration();
}
